import * as React from 'react';
import { memo, useMemo, useCallback, ReactNode } from 'react';
import { useRouter } from 'next/router';
import 'twin.macro';

import { MobileVersionFrontViewImpressionIllust } from '@restworld/ui-ds';
import { useAuth } from '@restworld/feature-authentication';

import { LayoutWrapper } from '../../layouts';
import { HomePageSignup } from './blocks/homepage-signup';
import { HomePageWrapper } from '../common/homepage-wrapper';
import { SignupComponentModal } from '../signup/signup-component-modal';
import { ContactCustomerView } from './blocks/contact-customer-view';
import { SeasonalJobPositions } from './blocks/seasonal-job-positions';
import { WhyRestworld } from './blocks/why-restworld';
import { JobPositionsOfStaticOccupation, TopLocalites, DesktopFrontViewImpressionWithSearchBar } from '.';
import { JobPositionsPerDomain } from './blocks/job-positions-per-domain';
import { HomeStaticPropsType } from '../../pages';

interface IHomeLayoutProps extends HomeStaticPropsType {
  children?: ReactNode;
}
export const HomeLayout: React.FunctionComponent<IHomeLayoutProps> = ({
  staticOccupations,
  jobsPerDomains,
  popularLocalities,
  children,
}) => {
  const router = useRouter();
  const { isAuthenticated, user } = useAuth();
  const redirectToPath = useCallback(
    (path: string, query?: { [key: string]: string | boolean }) => {
      router.push({
        pathname: path,
        query: query,
      });
    },
    [router]
  );
  const showSignupModal = useMemo(
    () => router?.query?.showSignupModal?.toString() || '',
    [router?.query?.showSignupModal]
  );
  return (
    <LayoutWrapper
      title="La startup per la ristorazione"
      hasFooter
      hasNavbar
      navbarVariant="home-page"
      showSearchBarInNavbar={isAuthenticated ? user?.type !== 'employer' : true}
      noHorizontalPadding
      testId="navbar-for-home-page"
      seoProps={{
        title: 'Restworld | La startup per la ristorazione',
        description:
          "Restworld è la piattaforma che mette in contatto professionista e aziende del mondo della ristorazione e dell'ospitalità",
      }}
    >
      <HomePageWrapper>
        <MobileVersionFrontViewImpressionIllust />
        <DesktopFrontViewImpressionWithSearchBar />
        <JobPositionsOfStaticOccupation staticOccupations={staticOccupations} testId="homepage-static-jobs-list" />
        <SeasonalJobPositions />
        {!isAuthenticated ? <HomePageSignup /> : null}
        <JobPositionsPerDomain {...{ redirectToPath, jobsPerDomains }} />
        <TopLocalites {...{ popularLocalities, redirectToPath }} />
      </HomePageWrapper>
      <WhyRestworld />
      <ContactCustomerView />
      {children}
      {showSignupModal === 'true' && <SignupComponentModal />}
    </LayoutWrapper>
  );
};

export default memo(HomeLayout);
